import "./ui.css";

interface ProgressRingProps {
	/* 0-100. Clamped, so a caller that computes 103% from stale counts renders a
	   full ring rather than an inverted arc. */
	pct: number;
	/* Caption under the percentage, e.g. "closed", "reconciled". */
	label: string;
	size?: number;
	/**
	 * "forest" (the default) is the lime-on-translucent-white ring the dark
	 * surfaces need. "light" is the same ring for a white card: a grey track
	 * with a primary-green arc, and its text in the page's own ink.
	 *
	 * A prop rather than a second component on purpose (DESIGN.md): the ring's
	 * geometry, clamping and progressbar semantics are the part worth sharing,
	 * and a fork is how two of them drift.
	 */
	tone?: "forest" | "light";
	/** Arc width. 10 suits the 104px default; a smaller ring wants less. */
	stroke?: number;
}

/* FOREST SURFACES ONLY — same rule as EmptyState tone="forest". The ring draws
   in lime on a translucent-white track, which is legible on --color-surface-dark
   and invisible on the light canvas. Extracted from pages/firm FirmCloseRing,
   which had a hardcoded "closed" caption and hardcoded #cfff91; the dashboard
   had built its own copy (.dash-hero-ring) rather than reuse it. */
export function ProgressRing({
	pct,
	label,
	size = 104,
	tone = "forest",
	stroke = 10,
}: ProgressRingProps) {
	const radius = (size - stroke) / 2;
	const circumference = 2 * Math.PI * radius;
	const clamped = Math.max(0, Math.min(100, pct));
	const offset = circumference * (1 - clamped / 100);
	const center = size / 2;

	return (
		// The arc is drawn in SVG and the percentage is plain text, so without this
		// the ring announces as two loose strings. Declaring the semantics on the
		// primitive means every consumer inherits them. `aria-label` carries the
		// name because progressbar is not a name-from-content role — the visible
		// "42% closed" inside is not read as the label.
		<div
			className={`ui-ring${tone === "light" ? " ui-ring--light" : ""}`}
			style={{ width: size, height: size }}
			role="progressbar"
			aria-label={label}
			aria-valuenow={clamped}
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuetext={`${clamped}% ${label}`}
		>
			<svg
				width={size}
				height={size}
				viewBox={`0 0 ${size} ${size}`}
				aria-hidden="true"
			>
				<circle
					cx={center}
					cy={center}
					r={radius}
					fill="none"
					stroke={
						tone === "light" ? "var(--color-border)" : "rgba(255,255,255,.12)"
					}
					strokeWidth={stroke}
				/>
				<circle
					cx={center}
					cy={center}
					r={radius}
					fill="none"
					stroke={
						tone === "light"
							? "var(--color-primary)"
							: "var(--color-accent-lime)"
					}
					strokeWidth={stroke}
					strokeLinecap="round"
					strokeDasharray={circumference}
					strokeDashoffset={offset}
					transform={`rotate(-90 ${center} ${center})`}
				/>
			</svg>
			<div className="ui-ring-center">
				<div className="ui-ring-value">{clamped}%</div>
				<div className="ui-ring-label">{label}</div>
			</div>
		</div>
	);
}
